import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { getIcon, getColor } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, Link2, Settings2, SearchX, Lock } from 'lucide-react';

export default function Viewer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const openedRef = useRef(false);

  const { data: app, isLoading, isError, error } = useQuery({
    queryKey: ['app', slug],
    queryFn: async () => (await api.get(`/apps/slug/${slug}`)).data,
    retry: false,
  });

  const status = error?.response?.status;
  const notFound = isError && status === 404;
  const denied = isError && status === 403;

  const launch = () => {
    if (app?.url) window.open(app.url, '_blank', 'noopener,noreferrer');
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/${slug}`);
    toast.success('Shortcut link copied', { description: `/${slug}` });
  };

  // Auto-launch once + record the open. When arriving via a typed URL there is no
  // user gesture, so the pop-up may be blocked — the "Open" button below is the fallback.
  useEffect(() => {
    if (app?.id && app?.url && !openedRef.current) {
      openedRef.current = true;
      api.post(`/apps/${app.id}/open`).catch(() => {});
      window.open(app.url, '_blank', 'noopener,noreferrer');
    }
  }, [app?.id, app?.url]);

  const Icon = getIcon(app?.icon);
  const color = getColor(app?.color);

  return (
    <div className="cx-page min-h-screen flex flex-col">
      <div className="h-14 flex items-center px-3 sm:px-4 border-b border-[color:var(--cx-border)] bg-[rgba(7,10,15,0.7)] backdrop-blur-xl">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="viewer-back-button">
          <ArrowLeft className="h-4 w-4 mr-1.5" />Back to dashboard
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        {isLoading && (
          <div className="w-full max-w-md" data-testid="viewer-loading-skeleton">
            <Skeleton className="h-56 w-full rounded-2xl bg-[color:var(--cx-surface-solid)]" />
          </div>
        )}

        {notFound && (
          <CenterPanel testid="viewer-notfound-panel" icon={<SearchX className="h-7 w-7 text-[color:var(--cx-muted)]" />}
            title={`No app at /${slug}`}
            desc="This shortcut isn't linked to any app. Check the address or add it in Apps.">
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/')}><ArrowLeft className="h-4 w-4 mr-1.5" />Dashboard</Button>
              <Button variant="secondary" onClick={() => navigate('/admin')}><Settings2 className="h-4 w-4 mr-1.5" />Manage apps</Button>
            </div>
          </CenterPanel>
        )}

        {denied && (
          <CenterPanel testid="viewer-denied-panel" icon={<Lock className="h-7 w-7 text-[color:var(--cx-muted)]" />}
            title="No access to this app"
            desc="You don't have access to this tool. Ask an admin to grant it to your account.">
            <Button onClick={() => navigate('/')}><ArrowLeft className="h-4 w-4 mr-1.5" />Back to dashboard</Button>
          </CenterPanel>
        )}

        {!isLoading && !isError && app && !app.url && (
          <CenterPanel testid="viewer-not-configured-panel" icon={<Settings2 className="h-7 w-7 text-[color:var(--cx-muted)]" />}
            title="URL not configured yet"
            desc={`${app.name} doesn't have a link set. Add its URL in Apps to launch it.`}>
            <Button onClick={() => navigate('/admin')}><Settings2 className="h-4 w-4 mr-1.5" />Configure in Apps</Button>
          </CenterPanel>
        )}

        {!isLoading && !isError && app && app.url && (
          <div className="cx-glass rounded-2xl p-8 max-w-md w-full text-center shadow-[var(--cx-shadow-soft)]" data-testid="viewer-launch-panel">
            <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center border border-[color:var(--cx-border)]"
              style={{ background: `linear-gradient(135deg, ${color.from}22, ${color.to}14)` }}>
              <Icon className="h-8 w-8" style={{ color: color.from }} />
            </div>
            <h3 className="mt-4 font-display text-xl text-[color:var(--cx-fg)]">{app.name}</h3>
            <p className="mt-1.5 text-sm text-[color:var(--cx-fg-muted)]">
              Opening in a new window… If nothing happened, your browser may have blocked the pop-up — use the button below.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button onClick={launch} data-testid="viewer-launch-button"><ExternalLink className="h-4 w-4 mr-1.5" />Open {app.name}</Button>
              <Button variant="secondary" onClick={copyLink} data-testid="viewer-copy-link-button"><Link2 className="h-4 w-4 mr-1.5" />Copy link</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CenterPanel({ icon, title, desc, children, testid }) {
  return (
    <div className="px-4" data-testid={testid}>
      <div className="cx-glass rounded-2xl p-8 max-w-md w-full text-center shadow-[var(--cx-shadow-soft)]">
        <div className="mx-auto h-12 w-12 rounded-xl bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center">
          {icon}
        </div>
        <h3 className="mt-4 font-display text-lg text-[color:var(--cx-fg)]">{title}</h3>
        <p className="mt-1.5 text-sm text-[color:var(--cx-fg-muted)]">{desc}</p>
        <div className="mt-6 flex items-center justify-center">{children}</div>
      </div>
    </div>
  );
}
